import {PlateElement, useEditorRef, useElement, useRemoveNodeButton} from "platejs/react";
import styles from "../styles/SlateEditor.module.scss";
import * as Popover from "@radix-ui/react-popover";
import {ButtonWrapper} from "../toolbar/buttons/_Button";
import FontAwesome from "../../utilities/FontAwesome";
import {setColumns} from '@platejs/layout';
import { useDebouncePopoverOpen } from '@platejs/layout/react';
import classNames from "classnames";
import {PlateElementProps} from "@platejs/core/react";
import {TColumnElement} from "platejs";

export const validColumnWidthClassNames = ["full", "half", "two-third", "two-fourth", "two-fifth", "two-sixth", "third", "fourth", "fifth", "sixth"] as const;
type IValidWidthClassName = typeof validColumnWidthClassNames[number];
type IValidWidthPercents = "100.0%" | "50.0%" | "66.7%" | "75.0%" | "80.0%" | "83.3%" | "33.3%" | "25.0%" | "20.0%" | "16.6%";

export function columnWidthClassNameToPercentage(text: IValidWidthClassName): IValidWidthPercents {
    switch (text) {
        case "full":
            return "100.0%";
        case "half":
            return "50.0%";
        case "two-third":
            return "66.7%";
        case "two-fourth":
            return "75.0%";
        case "two-fifth":
            return "80.0%";
        case "two-sixth":
            return "83.3%";
        case "third":
            return "33.3%";
        case "fourth":
            return "25.0%";
        case "fifth":
            return "20.0%";
        case "sixth":
            return "16.6%";
        default:
            return "100.0%";
    }
}

export function columnWidthPercentageToClassName(percent: IValidWidthPercents): IValidWidthClassName {
    const p = Number.parseFloat(percent).toFixed(1) + "%";
    switch (p) {
        case "100.0%":
            return "full";
        case "50.0%":
            return "half";
        case "66.7%":
            return "two-third";
        case "75.0%":
            return "two-fourth";
        case "80.0%":
            return "two-fifth";
        case "83.3%":
            return "two-sixth";
        case "33.3%":
            return "third";
        case "25.0%":
            return "fourth";
        case "20.0%":
            return "fifth";
        case "16.6%":
            return "sixth";
        default:
            return "full";
    }
}

export function widthFromClassList(classList: DOMTokenList): IValidWidthPercents {
    let width = "100.0%" as IValidWidthClassName;
    Array.from(classList).forEach(c => {
        if (validColumnWidthClassNames.includes(c as IValidWidthClassName)) {
            width = columnWidthClassNameToPercentage(c as IValidWidthClassName) as IValidWidthClassName;
        }
    })
    return width as IValidWidthPercents;
}

export const DoubleColumnOutlined = (props) => (
    <svg
        fill="none"
        height="16"
        viewBox="0 0 16 16"
        width="16"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <path
            clipRule="evenodd"
            d="M8.5 3H13V13H8.5V3ZM7.5 2H8.5H13C13.5523 2 14 2.44772 14 3V13C14 13.5523 13.5523 14 13 14H8.5H7.5H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H7.5ZM7.5 13H3L3 3H7.5V13Z"
            fill="currentColor"
            fillRule="evenodd"
        />
    </svg>
);

const ThreeColumnOutlined = (props) => (
    <svg
        fill="none"
        height="16"
        viewBox="0 0 16 16"
        width="16"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <path
            clipRule="evenodd"
            d="M9.25 3H6.75V13H9.25V3ZM9.25 2H6.75H5.75H3C2.44772 2 2 2.44772 2 3V13C2 13.5523 2.44772 14 3 14H5.75H6.75H9.25H10.25H13C13.5523 14 14 13.5523 14 13V3C14 2.44772 13.5523 2 13 2H10.25H9.25ZM10.25 3V13H13V3H10.25ZM3 13H5.75V3H3L3 13Z"
            fill="currentColor"
            fillRule="evenodd"
        />
    </svg>
);

const RightSideDoubleColumnOutlined = (props) => (
    <svg
        fill="none"
        height="16"
        viewBox="0 0 16 16"
        width="16"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <path
            clipRule="evenodd"
            d="M11.25 3H13V13H11.25V3ZM10.25 2H11.25H13C13.5523 2 14 2.44772 14 3V13C14 13.5523 13.5523 14 13 14H11.25H10.25H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H10.25ZM10.25 13H3L3 3H10.25V13Z"
            fill="currentColor"
            fillRule="evenodd"
        />
    </svg>
);

const LeftSideDoubleColumnOutlined = (props) => (
    <svg
        fill="none"
        height="16"
        viewBox="0 0 16 16"
        width="16"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <path
            clipRule="evenodd"
            d="M5.75 3H13V13H5.75V3ZM4.75 2H5.75H13C13.5523 2 14 2.44772 14 3V13C14 13.5523 13.5523 14 13 14H5.75H4.75H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H4.75ZM4.75 13H3L3 3H4.75V13Z"
            fill="currentColor"
            fillRule="evenodd"
        />
    </svg>
);

const DoubleSideDoubleColumnOutlined = (props) => (
    <svg
        fill="none"
        height="16"
        viewBox="0 0 16 16"
        width="16"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <path
            clipRule="evenodd"
            d="M10.25 3H5.75V13H10.25V3ZM10.25 2H5.75H4.75H3C2.44772 2 2 2.44772 2 3V13C2 13.5523 2.44772 14 3 14H4.75H5.75H10.25H11.25H13C13.5523 14 14 13.5523 14 13V3C14 2.44772 13.5523 2 13 2H11.25H10.25ZM11.25 3V13H13V3H11.25ZM3 13H4.75V3H3L3 13Z"
            fill="currentColor"
            fillRule="evenodd"
        />
    </svg>
);

export function ColumnGroup(props: PlateElementProps) {
    const isOpen = useDebouncePopoverOpen();
    const editor = useEditorRef();
    const element = useElement<TColumnElement>();
    const { props: buttonProps } = useRemoveNodeButton({ element });

    const onColumnChange = (widths: IValidWidthPercents[]) => {
        setColumns(editor, {
            at: element,
            widths,
        });
    };

    return <PlateElement {...props}>
        <Popover.Root open={isOpen}>
            <Popover.Trigger asChild>
                <div className={"row clearfix"}>
                    {props.children}
                </div>
            </Popover.Trigger>
            <Popover.Content className={styles.columnPopoverContent} onOpenAutoFocus={(e) => e.preventDefault()}>
                <ButtonWrapper onClick={() => onColumnChange(["50.0%", "50.0%"])}>
                    <DoubleColumnOutlined />
                </ButtonWrapper>
                <ButtonWrapper onClick={() => onColumnChange(['33.3%', '33.3%', '33.3%'])}>
                    <ThreeColumnOutlined />
                </ButtonWrapper>
                <ButtonWrapper onClick={() => onColumnChange(['66.7%', '33.3%'])}>
                    <RightSideDoubleColumnOutlined />
                </ButtonWrapper>
                <ButtonWrapper onClick={() => onColumnChange(['33.3%', '66.7%'])}>
                    <LeftSideDoubleColumnOutlined />
                </ButtonWrapper>
                <ButtonWrapper onClick={() => onColumnChange(['25.0%', '50.0%', '25.0%'])}>
                    <DoubleSideDoubleColumnOutlined />
                </ButtonWrapper>
                <ButtonWrapper {...buttonProps}>
                    <FontAwesome prefix={"far"} fixedWidth name={"fa-trash-can"} />
                </ButtonWrapper>
            </Popover.Content>
        </Popover.Root>
    </PlateElement>
}

export function ColumnGroupStatic(props: PlateElementProps) {
    return <div className={"row clearfix"}>
        {props.children}
    </div>
}

export function ColumnItemStatic(props: PlateElementProps) {
    const { width, flowOpposite } = props.element;

    const columnItemClassName = classNames({
        ["column"]: true,
        ["flow-opposite"]: flowOpposite,
        [columnWidthPercentageToClassName(width as IValidWidthPercents) as IValidWidthClassName]: true,
    })

    return <div className={columnItemClassName}>
        {props.children}
    </div>
}

export function ColumnItem(props: PlateElementProps) {
    const { width, flowOpposite } = props.element;

    const columnItemClassName = classNames({
        [styles.columnItemEditing]: true,
        ["column"]: true,
        ["flow-opposite"]: flowOpposite,
        [columnWidthPercentageToClassName(width as IValidWidthPercents) as IValidWidthClassName]: true,
    })

    return <PlateElement
        {...props}
        className={columnItemClassName}
    >
        {props.children}
    </PlateElement>
}
